/* eslint-disable jsx-a11y/anchor-is-valid */
import {FC, useEffect, useState} from 'react'
import {PageTitle} from '../../../../_metronic/layout/core'
import {Datatable} from '../../../components/Datatable/Datatable'
import {Input} from '../../../components/Input/Input'
import {Loading} from '../../../components/Loading/Loading'
import {GET} from '../../../services/api'
import Swal from 'sweetalert2'
import {i18n} from '../../../translate/i18n'
import {BreadCrumbs} from '../../../components/BreadCrumbs/breadCrumbrs'

// Interface atualizada para refletir a relação com a categoria
interface ReviewHotel {
  id: number
  pluginspaceId: number
  review: string | null
  rating: number | null
  type: string
  status: boolean
  created: string
  updated: string
  reviewHotelCategoryId?: number // Novo campo
  category?: {title: string} // Novo campo vindo do JOIN
  guest?: {id: number; name: string; email: string}
  form?: {id: number; title: string}
}

const ContaContent: FC = () => {
  const [loading, setLoading] = useState(false)
  const [reviews, setReviews] = useState<ReviewHotel[]>([])
  const [filteredReviews, setFilteredReviews] = useState<ReviewHotel[]>([])

  // Estados de filtro
  const [categories, setCategories] = useState<any[]>([])
  const [selectedCategory, setSelectedCategory] = useState<string>('all')
  const [selectedRating, setSelectedRating] = useState<string>('all')

  const getInitialData = async () => {
    try {
      setLoading(true)
      // Buscamos reviews e categorias
      const [resReviews, resCats] = await Promise.all([
        GET('/review-hotel'),
        GET('/review-hotel-category'),
      ])

      setCategories(resCats.data || [])

      if (resReviews.success && resReviews.data) {
        const processedData = resReviews.data.map((item: ReviewHotel) => {
          const hasRating = item.rating !== null && item.rating !== undefined

          const reviewText = item.review || ''
          const preview =
            reviewText.length > 100 ? `${reviewText.substring(0, 100)}...` : reviewText || '-'

          return {
            ...item,
            date: new Date(item.created).toLocaleString('pt-BR'),
            categoryTitle: item.category?.title || 'Geral',
            title: item?.form?.title || '-',
            reviewPreview: preview,
            stars: hasRating
              ? `<span class="fs-6 fw-bold ${getRatingColor(item.rating)}">${generateStarsDisplay(
                  item.rating
                )} (${Number(item.rating).toFixed(1)}/5)</span>`
              : '<span class="text-muted">-</span>',
          }
        })
        setReviews(processedData)
        setFilteredReviews(processedData)
      }
    } catch (e) {
      console.error('Erro ao carregar dados:', e)
    } finally {
      setLoading(false)
    }
  }

  const applyFilters = () => {
    let filtered = [...reviews]
    if (selectedCategory !== 'all') {
      filtered = filtered.filter((r) => String(r.reviewHotelCategoryId) === selectedCategory)
    }
    if (selectedRating !== 'all') {
      filtered = filtered.filter(
        (r) => r.rating !== null && Math.floor(r.rating) === parseInt(selectedRating)
      )
    }
    setFilteredReviews(filtered)
  }

  useEffect(() => {
    applyFilters()
  }, [selectedCategory, selectedRating, reviews])
  useEffect(() => {
    getInitialData()
  }, [])

  // Funções de formatação mantidas igual ao padrão
  const generateStarsDisplay = (rating: number | null): string => {
    if (rating === null) return '☆☆☆☆☆'
    const fullStarsCount = Math.floor(rating)
    return '★'.repeat(fullStarsCount) + '☆'.repeat(5 - fullStarsCount)
  }

  const getRatingColor = (rating: number | null): string => {
    if (rating === null) return 'text-muted'
    if (rating >= 4) return 'text-success'
    if (rating >= 3) return 'text-warning'
    return 'text-danger'
  }

  const stats = {
    total: reviews.length,
    averageRating:
      reviews.length > 0
        ? (reviews.reduce((s, r) => s + (r.rating || 0), 0) / reviews.length).toFixed(1)
        : '0',
  }

  return (
    <>
      {loading && <Loading />}
      <div className='card py-4 px-11'>
        <div className='card-header border-0 pt-5'>
          <h3 className='card-title align-items-start flex-column'>
            <span className='card-label fw-bolder fs-3 mb-1'>
              {i18n.t('hospitality_review.page_title')}
            </span>
            <BreadCrumbs />
          </h3>
        </div>
        <div className='card-body'>
          {/* Cards de Estatísticas */}
          <div className='row mb-10'>
            <div className='col-md-3'>
              <div className='card card-flush shadow-sm bg-light-primary'>
                <div className='card-body text-center d-flex align-items-center justify-content-center gap-4 py-4'>
                  <i className='fas fa-comments fs-2x text-primary'></i>
                  <div className='text-start'>
                    <h4 className='fw-bold text-gray-800 m-0'>{stats.total}</h4>
                    <span className='text-muted fw-semibold'>Total</span>
                  </div>
                </div>
              </div>
            </div>
            <div className='col-md-3'>
              <div className='card card-flush shadow-sm bg-light-info'>
                <div className='card-body text-center d-flex align-items-center justify-content-center gap-4 py-4'>
                  <i className='fas fa-star fs-2x text-info'></i>
                  <div className='text-start'>
                    <h4 className='fw-bold text-gray-800 m-0'>{stats.averageRating}</h4>
                    <span className='text-muted fw-semibold'>Média</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Filtros */}
          <div className='row mb-6'>
            <div className='col-md-3'>
              <Input
                inputAttr={{type: 'select'}}
                label='Categoria'
                options={[
                  {value: 'all', select: 'Todas'},
                  ...categories.map((c) => ({value: String(c.id), select: c.title})),
                ]}
                value={selectedCategory}
                change={setSelectedCategory}
              />
            </div>
            <div className='col-md-3'>
              <Input
                inputAttr={{type: 'select'}}
                label='Estrelas'
                options={[
                  {value: 'all', select: 'Todas'},
                  {value: '5', select: '5 Estrelas'},
                  {value: '4', select: '4 Estrelas'},
                  {value: '3', select: '3 Estrelas'},
                ]}
                value={selectedRating}
                change={setSelectedRating}
              />
            </div>
          </div>

          <Datatable
            data={filteredReviews}
            headers={[
              {key: 'Rating', value: 'stars', type: 'html'},
              {key: 'Formulário', value: 'title', type: 'default'},
              {key: 'Categoria', value: 'categoryTitle', type: 'default'},
              {key: 'Comentário', value: 'reviewPreview', type: 'default'},
              {key: 'Data', value: 'date', type: 'default'},
              {key: 'Ações', value: 'acoes', type: ''},
            ]}
            options={[
              {
                icon: 'fas fa-eye',
                title: 'Ver Detalhes',
                action: (item) => {
                  Swal.fire({
                    title: '<div class="fs-4 fw-bold">Detalhes da Avaliação</div>',
                    width: 600,
                    html: `
                      <div class="container-fluid text-start p-4">
                        <div class="mb-4"><label class="text-muted fs-7">Categoria</label><div class="fw-bold">${
                          item.categoryTitle
                        }</div></div>
                        <div class="mb-4"><label class="text-muted fs-7">Comentário</label><div class="p-3 bg-light rounded">${
                          item.review || 'Sem comentário.'
                        }</div></div>
                      </div>
                    `,
                  })
                },
              },
            ]}
            color='primary'
          />
        </div>
      </div>
    </>
  )
}

const ReviewsHotelResults: FC = () => (
  <>
    <PageTitle breadcrumbs={[]}>{i18n.t('hospitality_review.page_title')}</PageTitle>
    <ContaContent />
  </>
)

export {ReviewsHotelResults}
